import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createEmployerWorkspaceAdapter,
  endEmployerTrainingRun,
  getEmployerTrainingOverview,
  listEmployerTrainingScenarios,
  queryEmployerLiveCoach,
  queryEmployerTrainingCoach,
  restartEmployerTrainingRun,
  startEmployerTrainingRun,
} from "../../services/employer/employerWorkspaceAdapters";

const EmployerModeContext = createContext(null);

function normalizeCoachMessage(reply, question, quickAction) {
  if (!reply) return null;
  return {
    id: `local-${Date.now()}`,
    question: question || "",
    quick_action: quickAction || null,
    answer: reply.answer || "",
    recommended_next_step: reply.recommended_next_step || "",
    caution: reply.caution || "",
    draft_label: reply.draft_label || "",
    draft_text: reply.draft_text || "",
    draft_disclaimer: reply.draft_disclaimer || "",
    source_mode: reply.source_mode || "fallback",
    created_at: new Date().toISOString(),
  };
}

function buildCoachContextSignature({ mode, routePath, screenKey, adId, applicationId }) {
  return JSON.stringify({
    mode: mode || "live",
    route_path: routePath || "",
    screen_key: screenKey || "",
    ad_id: adId ?? null,
    application_id: applicationId ?? null,
  });
}

export function EmployerModeProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [currentRun, setCurrentRun] = useState(null);
  const [latestCompletedRun, setLatestCompletedRun] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [error, setError] = useState("");
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError, setCoachError] = useState("");
  const [coachSurfaceContext, setCoachSurfaceContext] = useState({});
  const [coachMessages, setCoachMessages] = useState([]);
  const coachRequestSeqRef = useRef(0);
  const coachContextSignatureRef = useRef("");
  const lastLiveContextResetRef = useRef("");

  const mode = currentRun?.status === "active" ? "training" : "live";
  const routePath = `${location.pathname}${location.search}`;
  const coachContextSignature = useMemo(() => buildCoachContextSignature({
    mode,
    routePath,
    screenKey: coachSurfaceContext?.screen_key,
    adId: coachSurfaceContext?.ad_id,
    applicationId: coachSurfaceContext?.application_id,
  }), [
    mode,
    routePath,
    coachSurfaceContext?.screen_key,
    coachSurfaceContext?.ad_id,
    coachSurfaceContext?.application_id,
  ]);

  const refreshTraining = useCallback(async () => {
    try {
      setLoading(true);
      const [overview, nextScenarios] = await Promise.all([
        getEmployerTrainingOverview(),
        listEmployerTrainingScenarios(),
      ]);
      setCurrentRun(overview?.current || null);
      setLatestCompletedRun(overview?.latest_completed || null);
      setRecentRuns(Array.isArray(overview?.recent_runs) ? overview.recent_runs : []);
      setScenarios(nextScenarios || []);
      setError("");
      return overview?.current || null;
    } catch (err) {
      setError(err.response?.data?.message || "Trainingsmodus konnte nicht geladen werden.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshTraining();
  }, [refreshTraining]);

  useEffect(() => {
    if (currentRun?.status === "active" && currentRun?.coach_history?.length) {
      setCoachMessages(currentRun.coach_history);
      return;
    }
    if (!currentRun || currentRun.status !== "active") {
      setCoachMessages([]);
    }
  }, [currentRun]);

  useEffect(() => {
    if (!currentRun || currentRun.status !== "active") return undefined;
    const onFocus = () => {
      refreshTraining();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [currentRun, refreshTraining]);

  useEffect(() => {
    coachContextSignatureRef.current = coachContextSignature;
    setCoachError("");
    coachRequestSeqRef.current += 1;

    if (mode === "live" && lastLiveContextResetRef.current !== coachContextSignature) {
      lastLiveContextResetRef.current = coachContextSignature;
      setCoachMessages([]);
    }
  }, [coachContextSignature, mode]);

  const startTrainingForJobAd = useCallback(async (jobAdId, scenarioKey) => {
    const run = await startEmployerTrainingRun({ jobAdId, scenarioKey });
    setCurrentRun(run);
    setError("");
    setCoachOpen(true);
    if (run?.resume_state?.route) {
      navigate(run.resume_state.route);
    } else if (run?.job_ad_snapshot?.id) {
      navigate(`/dashboard/employer/candidates?adId=${encodeURIComponent(run.job_ad_snapshot.id)}&training=1`);
    }
    return run;
  }, [navigate]);

  const resumeTraining = useCallback(() => {
    if (currentRun?.resume_state?.route) {
      navigate(currentRun.resume_state.route);
      return;
    }
    if (currentRun?.job_ad_snapshot?.id) {
      navigate(`/dashboard/employer/candidates?adId=${encodeURIComponent(currentRun.job_ad_snapshot.id)}&training=1`);
    }
  }, [currentRun, navigate]);

  const restartTraining = useCallback(async (scenarioKey) => {
    if (!currentRun?.id) return null;
    const run = await restartEmployerTrainingRun({ runId: currentRun.id, scenarioKey });
    setCurrentRun(run);
    setError("");
    setCoachOpen(true);
    if (run?.resume_state?.route) {
      navigate(run.resume_state.route);
    }
    return run;
  }, [currentRun, navigate]);

  const endTraining = useCallback(async () => {
    if (!currentRun?.id) return null;
    await endEmployerTrainingRun(currentRun.id);
    await refreshTraining();
    setCurrentRun(null);
    setCoachOpen(false);
    setError("");
    navigate("/dashboard/employer");
    return true;
  }, [currentRun, navigate, refreshTraining]);

  const registerCoachSurface = useCallback((surface) => {
    setCoachSurfaceContext({
      route_path: routePath,
      ...(surface || {}),
    });
  }, [routePath]);

  const askEmployerCoach = useCallback(async ({ question, quickAction }) => {
    const activeMode = currentRun?.status === "active" ? "training" : "live";
    const requestSeq = coachRequestSeqRef.current + 1;
    coachRequestSeqRef.current = requestSeq;
    try {
      const surfaceContext = {
        route_path: routePath,
        ...(coachSurfaceContext || {}),
      };
      const signatureAtRequest = buildCoachContextSignature({
        mode: activeMode,
        routePath: surfaceContext.route_path,
        screenKey: surfaceContext.screen_key,
        adId: surfaceContext.ad_id,
        applicationId: surfaceContext.application_id,
      });

      if (activeMode === "live" && !surfaceContext.screen_key) {
        setCoachError("Der Live Coach braucht erst einen stabilen sichtbaren Arbeitskontext.");
        return null;
      }

      setCoachLoading(true);
      setCoachError("");
      const result = activeMode === "training"
        ? await queryEmployerTrainingCoach({
            runId: currentRun.id,
            question,
            quickAction,
            surfaceContext,
          })
        : await queryEmployerLiveCoach({
            question,
            quickAction,
            surfaceContext,
          });

      const contextStillCurrent = (
        coachRequestSeqRef.current === requestSeq
        && coachContextSignatureRef.current === signatureAtRequest
      );
      if (!contextStillCurrent) return null;

      if (result?.run) {
        setCurrentRun(result.run);
      }

      if (activeMode === "live" && result?.coachContext) {
        setCoachSurfaceContext({
          ...surfaceContext,
          ...result.coachContext,
        });
      }

      const message = normalizeCoachMessage(result?.reply, question, quickAction);
      if (message) {
        setCoachMessages((current) => [...current, message]);
      }
      return result?.reply || null;
    } catch (err) {
      const nextError = err.response?.data?.message || (activeMode === "training"
        ? "Der Training Coach konnte nicht antworten."
        : "Der Live Coach konnte nicht antworten.");
      if (coachRequestSeqRef.current === requestSeq) {
        setCoachError(nextError);
      }
      return null;
    } finally {
      if (coachRequestSeqRef.current === requestSeq) {
        setCoachLoading(false);
      }
    }
  }, [coachSurfaceContext, currentRun, routePath]);

  const value = useMemo(() => ({
    mode,
    isTraining: mode === "training",
    isLive: mode === "live",
    loading,
    error,
    currentRun,
    latestCompletedRun,
    recentRuns,
    scenarios,
    refreshTraining,
    startTrainingForJobAd,
    resumeTraining,
    restartTraining,
    endTraining,
    adapter: createEmployerWorkspaceAdapter({ mode, trainingRun: currentRun }),
    coachOpen,
    setCoachOpen,
    coachLoading,
    coachError,
    coachMessages,
    coachSurfaceContext,
    registerCoachSurface,
    askEmployerCoach,
    askTrainingCoach: askEmployerCoach,
  }), [
    mode,
    loading,
    error,
    currentRun,
    latestCompletedRun,
    recentRuns,
    scenarios,
    coachOpen,
    coachLoading,
    coachError,
    coachMessages,
    coachSurfaceContext,
    refreshTraining,
    startTrainingForJobAd,
    resumeTraining,
    restartTraining,
    endTraining,
    registerCoachSurface,
    askEmployerCoach,
  ]);

  return (
    <EmployerModeContext.Provider value={value}>
      {children}
    </EmployerModeContext.Provider>
  );
}

export function useEmployerMode() {
  const context = useContext(EmployerModeContext);
  if (!context) {
    throw new Error("useEmployerMode must be used within EmployerModeProvider");
  }
  return context;
}

export function useEmployerTrainingSession() {
  const {
    currentRun,
    latestCompletedRun,
    recentRuns,
    scenarios,
    startTrainingForJobAd,
    resumeTraining,
    restartTraining,
    endTraining,
    refreshTraining,
    loading,
    error,
  } = useEmployerMode();
  return {
    currentRun,
    latestCompletedRun,
    recentRuns,
    scenarios,
    startTrainingForJobAd,
    resumeTraining,
    restartTraining,
    endTraining,
    refreshTraining,
    loading,
    error,
  };
}

export function useEmployerDataAdapter() {
  return useEmployerMode().adapter;
}

export function useEmployerActionAdapter() {
  return useEmployerMode().adapter;
}

export function useEmployerCoach() {
  const {
    coachOpen,
    setCoachOpen,
    coachLoading,
    coachError,
    coachMessages,
    coachSurfaceContext,
    registerCoachSurface,
    askEmployerCoach,
    currentRun,
    isTraining,
    isLive,
    mode,
  } = useEmployerMode();

  return {
    coachOpen,
    setCoachOpen,
    coachLoading,
    coachError,
    coachMessages,
    coachSurfaceContext,
    registerCoachSurface,
    askEmployerCoach,
    currentRun,
    isTraining,
    isLive,
    mode,
  };
}

export function useEmployerTrainingCoach() {
  const coach = useEmployerCoach();
  return {
    ...coach,
    askTrainingCoach: coach.askEmployerCoach,
  };
}
